import BigNumber from 'bignumber.js'
import { useSWR } from 'modules/network/hooks/useSwr'
import { useMemo } from 'react'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useGraphDepositsSWR } from 'modules/network/fetchers/fetchDeposits'
import { useGraphWithdrawalsSWR } from 'modules/network/fetchers/fetchWithdrawals'

import { bn } from 'shared/utils/bignumber'
import { POOL_INVESTORS_COLLECTION } from '../utils/getPoolInvestorContract'
import { calcDepositPoolInfo } from '../utils/calcDepositPoolInfo'
import { useAssetsData } from './useAssetsData'
import { weiToNum, weiToStr } from 'modules/blockChain/utils/parseWei'
import { DEFAULT_DECIMALS } from '../constants/poolAssets'
import { flow, toString } from 'lodash'

export function useDepositsList() {
  const { chainId, walletAddress } = useWeb3()
  const { getAssetDecimals } = useAssetsData()

  const deposits = useSWR(`deposits-${chainId}-${walletAddress}`, async () => {
    const requestsPools = POOL_INVESTORS_COLLECTION.map(async Pool => {
      const pool = Pool.contract.connectRpc({ chainId })

      const res = await calcDepositPoolInfo({
        chainId,
        walletAddress: walletAddress!,
        pool,
      })
      const assetDecimals = getAssetDecimals(res.depositedAsset)
      return {
        ...res,
        risk: Pool.risk,
        depositedAmount: weiToStr(res.deposit, assetDecimals),
      }
    })

    const responses = await Promise.all(requestsPools)
    const formatted = responses.filter(i => Number(i.deposit) !== 0)
    return formatted
  })

  const depositsChainData = deposits.data
  const { data: depositsGraphData } = useGraphDepositsSWR()
  const { data: withdrawalsGraphData } = useGraphWithdrawalsSWR()

  const richedData = useMemo(() => {
    if (!depositsChainData) return null
    if (!depositsGraphData || !withdrawalsGraphData) {
      return depositsChainData.map(e => ({ ...e, accruedInterest: null }))
    }

    return depositsChainData.map(entity => {
      const assetDecimals = getAssetDecimals(entity.depositedAsset)

      const depositsFiltered = depositsGraphData.filter(
        d => d.pool.toLowerCase() === entity.poolAddress.toLowerCase(),
      )
      const withdrawalsFiltered = withdrawalsGraphData.filter(
        d => d.pool.toLowerCase() === entity.poolAddress.toLowerCase(),
      )

      const dSum = depositsFiltered.reduce((summ, curr) => {
        const amount = new bn(weiToStr(curr.amount, assetDecimals))
        return summ.plus(amount)
      }, new bn(0))

      const wSum = withdrawalsFiltered.reduce((summ, curr) => {
        const amount = new bn(weiToStr(curr.amount, assetDecimals))
        return summ.plus(
          amount.dividedBy(weiToNum(curr.dtPrice, DEFAULT_DECIMALS)),
        )
      }, new bn(0))

      const accruedInterest: string = flow([
        (d: string) => new bn(d).plus(wSum).minus(dSum),
        (i: BigNumber) =>
          i.isNegative() && i.gte(new bn(-0.1)) ? new bn(0) : i,
        toString,
      ])(entity.depositedAmount)

      console.group('Pool calcs:')
      console.log('Chain entity', entity)
      console.log('Current deposit amount', entity.depositedAmount)
      console.log(
        'Graph deposits',
        depositsFiltered,
        depositsFiltered.map(v => weiToStr(v.amount, assetDecimals)),
      )
      console.log(
        'Graph withdrawals',
        withdrawalsFiltered,
        withdrawalsFiltered.map(v => weiToStr(v.amount, assetDecimals)),
      )
      console.log('D sum (Graph deposits summ)', dSum.toString())
      console.log('W sum (Graph withdrawals summ)', wSum.toString())
      console.log('Interest (amount + Wsum - Dsum)', accruedInterest)
      console.groupEnd()

      return {
        ...entity,
        accruedInterest,
      }
    })
  }, [
    depositsGraphData,
    withdrawalsGraphData,
    depositsChainData,
    getAssetDecimals,
  ])

  return {
    ...deposits,
    data: richedData,
  }
}
